import { Box, Menu, MenuItem } from '@mui/material';
import type { PlanNode } from '@timelog/pm-shared';
import type { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { ContractChip } from './ContractChip';
import { useContractSelection } from './useContractSelection';

import { MaterialSymbol } from '@/components/MaterialSymbol';

const CHIP_PX = 16;

/**
 * A task's contract, on the row itself.
 *
 * The panel's picker (`ContractPicker`) is the one action the task insight
 * panel carries, and it sits at the bottom of it — below the hours bar, two
 * money tiles and five detail rows. The first pilot test found it too far down
 * to discover, and a row said nothing about its contract at all, so "which of
 * these tasks is unpriced?" could not be answered by looking.
 *
 * The letter badge answers it at a glance: F/T/R for a mapped task, a dashed
 * square with a + and "Add contract" after it for one that names no contract —
 * hours that count but never price (D7, BR-4). The + and the words are "Add
 * resources"' pattern on the line below (3 Nov, Mette). It shares `useContractSelection` with the panel, so the remap
 * preview applies identically from either surface.
 *
 * Leaves only. A parent's subtasks each map on their own (ADR 0033), which is
 * what the panel says in words where a row has no room to.
 */
export const RowContractChip = ({ node }: { node: PlanNode }) => {
  const { t } = useTranslation(['pmPlanning', 'pmCommon']);
  const {
    anchor,
    contract,
    contractId,
    contractInputs,
    options,
    open,
    close,
    choose,
    remapDialog,
  } = useContractSelection(node);

  // A parent maps nothing itself; a project with no contracts has nothing to
  // offer, exactly as the panel's `detail-contract-none` branch says.
  if (node.children.length > 0 || contractInputs.length === 0) return null;

  const label = contract
    ? t('contract.ChipTitle', {
        name: contract.name,
        method: t(`pmCommon:billingMethod.${contract.type}`),
      })
    : t('contract.Unpriced');

  return (
    <>
      <Box
        component="button"
        type="button"
        title={label}
        aria-label={t('contract.AriaLabel', { label })}
        data-automation-id="row-contract-chip"
        data-mapped={contract !== undefined}
        /*
          The row's own click handler skips anything inside a `button`, but it
          only sees the event if this one lets it through — and a selection
          change here would close the menu as it opened.
        */
        onClick={(event: MouseEvent<HTMLElement>) => {
          event.stopPropagation();
          open(event.currentTarget);
        }}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1,
          flex: 'none',
          p: 0,
          fontFamily: 'var(--font-family)',
          '&:hover .row-contract-add': {
            borderColor: 'var(--color-blue-80)',
            color: 'var(--color-blue-80)',
          },
          border: 'none',
          bgcolor: 'transparent',
          cursor: 'pointer',
          borderRadius: '4px',
          '&:focus-visible': { outline: 'var(--focus-ring)', outlineOffset: '1px' },
        }}
      >
        {contract ? (
          <ContractChip type={contract.type} size={CHIP_PX} />
        ) : (
          /*
            Same footprint as the letter badge, so mapping a task does not
            reflow the name line — only fills the square in.
          */
          <>
            <Box
              component="span"
              className="row-contract-add"
              sx={{
                width: CHIP_PX,
                height: CHIP_PX,
                borderRadius: '4px',
                border: 'var(--border-width-thin) dashed var(--border-subtle)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flex: 'none',
                color: 'var(--fg-3)',
                transition: 'color var(--duration-instant), border-color var(--duration-instant)',
              }}
            >
              <MaterialSymbol name="add" size={12} />
            </Box>
            <Box
              component="span"
              data-automation-id="row-contract-add-label"
              sx={{ fontSize: 11, color: 'var(--fg-3)', whiteSpace: 'nowrap' }}
            >
              {t('contract.AddContract')}
            </Box>
          </>
        )}
      </Box>
      <Menu
        open={anchor !== null}
        anchorEl={anchor}
        onClose={close}
        slotProps={{ paper: { sx: { borderRadius: 'var(--border-radius-medium)' } } }}
      >
        {options.map((option) => (
          <MenuItem
            key={option.contractId ?? 'none'}
            selected={option.contractId === contractId}
            data-automation-id="contract-option"
            data-kind={option.kind}
            onClick={() => choose(option.contractId, option.label)}
            sx={{ gap: '9px', px: 1.5, py: '7px', fontSize: 14 }}
          >
            {option.type ? (
              <ContractChip type={option.type} size={18} />
            ) : (
              <Box component="span" sx={{ width: 18, height: 18, flex: 'none' }} />
            )}
            {option.label}
          </MenuItem>
        ))}
      </Menu>
      {remapDialog}
    </>
  );
};
